import React, { useState, useRef, useEffect } from 'react';
import Transition from '../utils/Transition';
import { getFirestore, collection, addDoc, deleteDoc, doc, getDocs, query, where } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';

// 1) Debounce utility
function debounce(fn, delay) {
  let timer = null;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

function ModalSearch({ id, modalOpen, setModalOpen, onStockSelect }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [recentStocks, setRecentStocks] = useState([]);
  const [followedStocks, setFollowedStocks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const modalRef = useRef(null);
  const db = getFirestore();
  const { user } = useAuth();

  useEffect(() => {
    if (modalOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [modalOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setModalOpen(false);
      }
    };

    if (modalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modalOpen]);

  useEffect(() => {
    if (user) {
      fetchFollowedStocks();
    }
  }, [user]);

  const fetchFollowedStocks = async () => {
    if (!user) return;
    try {
      const followedRef = collection(db, 'users', user.uid, 'followedStocks');
      const qFollowed = query(followedRef, where('uid', '==', user.uid));
      const querySnapshot = await getDocs(qFollowed);
      const stocks = querySnapshot.docs.map((docSnap) => docSnap.data().symbol);
      setFollowedStocks(stocks);
    } catch (error) {
      console.error('Error fetching followed stocks:', error);
    }
  };

  const handleFollowStock = async (symbol, e) => {
    e.stopPropagation();
    if (!user) {
      console.log('User is null; cannot follow stock');
      return;
    }
  
    // Optimistic update
    const isCurrentlyFollowed = followedStocks.includes(symbol);
    setFollowedStocks((prev) =>
      isCurrentlyFollowed
        ? prev.filter(s => s !== symbol)
        : [...prev, symbol]
    );
  
    try {
      const followedRef = collection(db, 'users', user.uid, 'followedStocks');
      const qFollowed = query(
        followedRef,
        where('uid', '==', user.uid),
        where('symbol', '==', symbol)
      );
      const querySnapshot = await getDocs(qFollowed);
  
      if (!querySnapshot.empty) {
        const docId = querySnapshot.docs[0].id;
        await deleteDoc(doc(db, 'followedStocks', docId));
      } else {
        await addDoc(followedRef, {
          uid: user.uid,
          symbol,
          timestamp: new Date(),
        });
      }
    } catch (error) {
      console.error('Error updating followed stocks:', error);
      // Rollback on error
      setFollowedStocks((prev) =>
        isCurrentlyFollowed
          ? [...prev, symbol]
          : prev.filter(s => s !== symbol)
      );
    }
  };

  // 2) Extract existing search logic into its own function
  const doSearch = async (value) => {
    setIsLoading(true);
    setError(null);
    let retries = 3;
  
    while (retries > 0) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch(
          `https://investorverden.onrender.com/api/search?q=${value}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            signal: controller.signal
          }
        );
        
        clearTimeout(timeoutId);
  
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
  
        const data = await response.json();
        setSuggestions(Array.isArray(data) ? data : []);
        setIsLoading(false);
        return;
      } catch (err) {
        console.error('Search error:', err);
        retries--;
        
        if (retries === 0) {
          setError('Der opstod en fejl under søgningen. Prøv igen senere.');
          setSuggestions([]);
          setIsLoading(false);
          return;
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    
    setIsLoading(false);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value); // Update input value immediately
    setError(null);
  
    // Debounce the actual search
    if (value.length > 1) {
      debouncedSearch(value);
    } else {
      setSuggestions([]);
    }
  };
  
  const debouncedSearch = debounce((value) => {
    doSearch(value);
  }, 500);
  
  const handleStockSelect = async (symbol) => {
    try {
      const fetchOptions = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      };
  
      const [timeSeriesResponse, companyResponse] = await Promise.all([
        fetch(`https://investorverden.onrender.com/api/stocks?q=${symbol}`, fetchOptions),
        fetch(`https://investorverden.onrender.com/api/company?q=${symbol}`, fetchOptions)
      ]);
  
      if (!timeSeriesResponse.ok) {
        throw new Error(`Stock API error: ${timeSeriesResponse.status}`);
      }
      if (!companyResponse.ok) {
        throw new Error(`Company API error: ${companyResponse.status}`);
      }
  
      const [timeSeriesData, companyData] = await Promise.all([
        timeSeriesResponse.json(),
        companyResponse.json()
      ]);
  
      const combinedData = {
        symbol,
        timeSeries: timeSeriesData.stockData,
        companyInfo: companyData,
      };
  
      if (typeof onStockSelect === 'function') {
        onStockSelect(combinedData);
      }
  
      setRecentStocks((prev) => {
        const updatedRecentStocks = [combinedData, ...prev.filter((stock) => stock.symbol !== symbol)];
        return updatedRecentStocks.slice(0, 4);
      });
  
      setModalOpen(false);
      setSearchQuery('');
      setSuggestions([]);
    } catch (error) {
      console.error('Error fetching stock and company data:', error);
      // Optional: Add error state handling here
    }
  };

  const filteredSuggestions = Array.isArray(suggestions)
    ? suggestions
        .filter((s) => s && s.symbol && s.name)
        .sort((a, b) => b.marketCap - a.marketCap)
        .filter(
          (s) =>
            (s.symbol && s.symbol.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (s.name && s.name.toLowerCase().includes(searchQuery.toLowerCase()))
        )
        .sort(
          (a, b) =>
            (b.symbol.endsWith('.CO') ? 1 : -1) - (a.symbol.endsWith('.CO') ? 1 : -1)
        )
    : [];

  return (
    <>
      <Transition
        className="fixed inset-0 bg-gray-900 bg-opacity-30 z-50 transition-opacity"
        show={modalOpen}
        enter="transition ease-out duration-200"
        enterStart="opacity-0"
        enterEnd="opacity-100"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        aria-hidden="true"
      />
      <Transition
        id={id}
        className="fixed inset-0 z-50 overflow-hidden flex items-start top-20 mb-4 justify-center px-4 sm:px-6"
        role="dialog"
        aria-modal="true"
        show={modalOpen}
        enter="transition ease-in-out duration-200"
        enterStart="opacity-0 translate-y-4"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-in-out duration-200"
        leaveStart="opacity-100 translate-y-0"
        leaveEnd="opacity-0 translate-y-4"
      >
        <div
          ref={modalRef}
          className="bg-white dark:bg-gray-800 border border-transparent dark:border-gray-700/60 overflow-auto max-w-2xl w-full max-h-full rounded-lg shadow-lg"
        >
          <form className="border-b border-gray-200 dark:border-gray-700/60">
            <div className="relative">
            <input
  ref={inputRef}
  className="w-full dark:text-gray-300 bg-white dark:bg-gray-800 border-0 focus:ring-transparent placeholder-gray-400 dark:placeholder-gray-500 appearance-none py-3 pl-10 pr-4"
  type="search"
  placeholder="Søg på aktier..."
  value={searchQuery}
  onChange={handleSearchChange}
/>
            </div>
          </form>

          <ul className="bg-white dark:bg-gray-800">
            {isLoading && (
              <li className="px-4 py-2 text-gray-500">
                Søger...
              </li>
            )}
            
            {error && (
              <li className="px-4 py-2 text-red-500">
                {error}
              </li>  
            )}

            {!isLoading && !error && searchQuery.length > 1 && 
             (!filteredSuggestions || filteredSuggestions.length === 0) && (
              <li className="px-4 py-2 text-gray-500">
                Ingen resultater
              </li>
            )}

            {!isLoading && !error && filteredSuggestions.map((suggestion, index) => (
              <li key={index} className="bg-white dark:bg-gray-800">
                <div className="flex items-center justify-between px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700/20">
                  <button
                    className="flex-grow text-left"
                    onClick={() => handleStockSelect(suggestion.symbol)}
                  >
                    {suggestion.shortName || suggestion.symbol} - {suggestion.name || 'Unknown Name'}
                  </button>
                  <button
                    onClick={(e) => handleFollowStock(suggestion.symbol, e)}
                    className="ml-2 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full"
                  >
                    {followedStocks.includes(suggestion.symbol) ? (
                      <svg
                        className="w-5 h-5 text-blue-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {recentStocks.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700/60 mt-4">
              <h3 className="px-4 py-2 text-lg font-semibold">Tidligere søgte aktier</h3>
              <ul>
                {recentStocks.map((stock, index) => (
                  <li key={index}>
                    <div className="flex items-center justify-between px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700/20">
                      <button
                        className="flex-grow text-left"
                        onClick={() => handleStockSelect(stock.symbol)}
                      >
                        {stock.companyInfo.shortName || stock.symbol} {stock.companyInfo.name || ''}
                      </button>
                      <button
                        onClick={(e) => handleFollowStock(stock.symbol, e)}
                        className="ml-2 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full"
                      >
                        {followedStocks.includes(stock.symbol) ? (
                          <svg
                            className="w-5 h-5 text-blue-500"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ) : (
                          <svg
                            className="w-5 h-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Transition>
    </>
  );
}

export default ModalSearch;